/**
 * Phase 125.B — Individual Student Plan page.
 *
 * Single-page generator + library. Teacher enters the goal +
 * accommodations on the left, the generated plan renders on the right,
 * and a slim "My Plans" panel surfaces previously-saved ones. Privacy
 * framing front-and-center: the page banner reminds teachers no
 * student data is stored unless they choose to save under an
 * anonymous label.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Eye,
  EyeOff,
  Layers,
  LifeBuoy,
  Loader2,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import AccommodationPicker from "../components/AccommodationPicker";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const REPORTING_PERIODS = [
  { id: "term_1", label: "Term 1" },
  { id: "term_2", label: "Term 2" },
];

const SUBJECTS = [
  "Language Arts", "Mathematics", "Science and Technology",
  "Social Studies", "History", "Geography", "Health and Physical Education",
  "The Arts", "French",
];

export default function IndividualStudentPlan() {
  // Form inputs.
  const [grade, setGrade] = useState("6");
  const [subject, setSubject] = useState("Language Arts");
  const [expectationCodes, setExpectationCodes] = useState("");
  const [learningGoal, setLearningGoal] = useState("");
  const [accommodations, setAccommodations] = useState({ selectedIds: [], otherText: "" });
  const [reportingPeriod, setReportingPeriod] = useState("term_1");
  const [optionalDetail, setOptionalDetail] = useState("");

  // Output state.
  const [plan, setPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branded, setBranded] = useState(true);

  // Save / library.
  const [studentLabel, setStudentLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);

  useEffect(() => {
    refreshSavedPlans();
  }, []);

  const refreshSavedPlans = async () => {
    setLoadingSaved(true);
    try {
      const res = await fetch(`${apiBase()}/individual-student-plan/list`, {
        credentials: "include",
      });
      if (res.status === 402) {
        // Free / lapsed user — silently leave the list empty.
        setSavedPlans([]);
        return;
      }
      if (!res.ok) throw new Error(`List failed (${res.status})`);
      const data = await res.json();
      setSavedPlans(data.plans || []);
    } catch (e) {
      toast.error(e?.message || "Couldn't load saved plans.");
    } finally {
      setLoadingSaved(false);
    }
  };

  const generate = async () => {
    if (!learningGoal.trim()) {
      toast.error("Add a learning goal first.");
      return;
    }
    setGenerating(true);
    setPlan(null);
    try {
      const codes = expectationCodes
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await fetch(`${apiBase()}/individual-student-plan/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grade,
          subject,
          expectation_codes: codes,
          learning_goal: learningGoal.trim(),
          accommodation_ids: accommodations.selectedIds || [],
          accommodation_other: accommodations.otherText || "",
          reporting_period: reportingPeriod,
          optional_detail: optionalDetail.trim(),
        }),
      });
      if (res.status === 402) {
        toast.error("This is a Pro feature. Upgrade to generate Individual Student Plans.");
        return;
      }
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || `Generate failed (${res.status})`);
      }
      setPlan(await res.json());
      toast.success("Plan generated. Review, then export or save.");
    } catch (e) {
      toast.error(e?.message || "Plan generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const exportPdf = async () => {
    if (!plan) return;
    setExporting(true);
    try {
      const res = await fetch(`${apiBase()}/individual-student-plan/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, branded }),
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `individual-student-plan${branded ? "" : "-unbranded"}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error(e?.message || "PDF export failed.");
    } finally {
      setExporting(false);
    }
  };

  const savePlan = async () => {
    if (!plan) return;
    const label = (studentLabel || `Student ${savedPlans.length + 1}`).trim();
    setSaving(true);
    try {
      const res = await fetch(`${apiBase()}/individual-student-plan/save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_label: label, plan }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      toast.success(`Saved as "${label}".`);
      setStudentLabel("");
      refreshSavedPlans();
    } catch (e) {
      toast.error(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const loadSavedPlan = async (planId) => {
    try {
      const res = await fetch(`${apiBase()}/individual-student-plan/${planId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const doc = await res.json();
      // Restore form inputs from the saved doc.
      setGrade(doc.grade || "");
      setSubject(doc.subject || "");
      setExpectationCodes((doc.expectation_codes || []).join(", "));
      setLearningGoal(doc.learning_goal || "");
      setAccommodations({
        selectedIds: doc.accommodation_ids || [],
        otherText: doc.accommodation_other || "",
      });
      setReportingPeriod(doc.reporting_period || "term_1");
      setOptionalDetail(doc.optional_detail || "");
      setPlan(doc.plan_body || null);
      setStudentLabel(doc.student_label || "");
      toast.success(`Loaded "${doc.student_label}"`);
    } catch (e) {
      toast.error(e?.message || "Load failed.");
    }
  };

  const deleteSavedPlan = async (planId, label) => {
    if (!window.confirm(`Delete "${label}"? This can't be undone.`)) return;
    try {
      const res = await fetch(`${apiBase()}/individual-student-plan/${planId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success(`Deleted "${label}".`);
      refreshSavedPlans();
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    }
  };

  const canGenerate = useMemo(
    () => !!learningGoal.trim() && !!grade && !!subject,
    [learningGoal, grade, subject],
  );

  return (
    <div className="min-h-screen bg-[#f9fafb] text-[#1c2d3d]">
      <header className="border-b border-[#4a6984]/10 bg-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              data-testid="isp-back"
              className="inline-flex items-center text-sm text-[#4a6984]/80 hover:text-[#1c2d3d]"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Link>
            <h1 className="text-xl font-extrabold tracking-tight">
              Individual Student Plan
            </h1>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 pb-3">
          <p className="text-xs text-[#4a6984]/80 italic">
            Goal in → term plan out. The student stays at grade level —
            this plan adjusts the scaffold, expectation count, and
            checkpoint. No student data is stored unless you choose to
            save under an anonymous label.
          </p>
          <Link
            to="/classroom-support"
            data-testid="isp-classroom-support-link"
            className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#4a6984] hover:text-[#1c2d3d] underline underline-offset-4 decoration-dotted"
          >
            <LifeBuoy className="h-3.5 w-3.5" />
            Browse Classroom Support (ADHD · Gifted · 2e) →
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left — form */}
        <section className="ae-card p-5 space-y-4" data-testid="isp-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-bold">
              Grade
              <input
                type="text"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                data-testid="isp-grade"
                className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#4a6984]/20 text-sm"
              />
            </label>
            <label className="text-xs font-bold">
              Subject
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="isp-subject"
                className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#4a6984]/20 text-sm"
              >
                {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          <label className="text-xs font-bold block">
            Reporting period
            <div className="mt-1 flex gap-2 flex-wrap">
              {REPORTING_PERIODS.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setReportingPeriod(p.id)}
                  data-testid={`isp-period-${p.id}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${
                    reportingPeriod === p.id
                      ? "bg-[#96ded1]/30 border-[#96ded1] text-[#1c2d3d]"
                      : "bg-white border-[#4a6984]/20 text-[#4a6984] hover:bg-[#96ded1]/10"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </label>

          <label className="text-xs font-bold block">
            Linked expectation code(s)
            <input
              type="text"
              value={expectationCodes}
              onChange={(e) => setExpectationCodes(e.target.value)}
              placeholder="e.g., C2.4, C3.5"
              data-testid="isp-expectations"
              className="mt-1 w-full px-2.5 py-1.5 rounded border border-[#4a6984]/20 text-sm"
            />
            <span className="text-[10px] text-[#4a6984]/60 font-normal italic block mt-0.5">
              Comma-separated. Use the codes that come up most for this goal.
            </span>
          </label>

          <label className="text-xs font-bold block">
            Learning goal (as written on the student's plan)
            <textarea
              value={learningGoal}
              onChange={(e) => setLearningGoal(e.target.value)}
              rows={3}
              placeholder='e.g., "The student will identify the main idea in a short text using a graphic organizer."'
              data-testid="isp-learning-goal"
              className="mt-1 w-full px-2.5 py-2 rounded border border-[#4a6984]/20 text-sm"
            />
          </label>

          <div>
            <div className="text-xs font-bold mb-1.5">
              Accommodations / strategies that work
            </div>
            <AccommodationPicker
              selectedIds={accommodations.selectedIds}
              otherText={accommodations.otherText}
              onChange={setAccommodations}
              testIdPrefix="isp-accommodation"
              collapsible
            />
          </div>

          <label className="text-xs font-bold block">
            Optional detail
            <textarea
              value={optionalDetail}
              onChange={(e) => setOptionalDetail(e.target.value)}
              rows={2}
              placeholder="Current reading level, what's been tried, interests… No student name needed."
              data-testid="isp-detail"
              className="mt-1 w-full px-2.5 py-2 rounded border border-[#4a6984]/20 text-sm"
            />
          </label>

          <button
            type="button"
            onClick={generate}
            disabled={!canGenerate || generating}
            data-testid="isp-generate"
            className="ae-cta inline-flex items-center justify-center w-full px-4 py-2.5 rounded-md text-sm font-extrabold disabled:opacity-40"
          >
            {generating ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating term plan…</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate term plan</>
            )}
          </button>
        </section>

        {/* Right — output + library */}
        <section className="space-y-5">
          <div className="ae-card p-5" data-testid="isp-output">
            {!plan && !generating ? (
              <div className="text-center py-12 text-[#4a6984]/60 text-sm">
                Your generated plan will appear here.
              </div>
            ) : null}
            {generating ? (
              <div className="text-center py-12">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#4a6984]" />
                <p className="mt-3 text-sm text-[#4a6984]">
                  Generating a term-long plan…
                </p>
              </div>
            ) : null}
            {plan ? (
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-extrabold text-[#4a6984]/80 mb-1">Goal</div>
                  <p className="text-sm" data-testid="isp-goal-restated">{plan.goal_restated}</p>
                </div>
                {plan.success_criteria?.length ? (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide font-extrabold text-[#4a6984]/80 mb-1">Success criteria</div>
                    <ul className="text-sm list-disc list-inside space-y-0.5">
                      {plan.success_criteria.map((sc, i) => (
                        <li key={i} data-testid={`isp-sc-${i}`}>{sc}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div className="space-y-2.5">
                  {(plan.phases || []).map((p) => (
                    <details
                      key={p.phase_number}
                      className="border border-[#4a6984]/15 rounded-md bg-white"
                      data-testid={`isp-phase-${p.phase_number}`}
                    >
                      <summary className="cursor-pointer px-3 py-2 text-sm font-bold flex items-center justify-between hover:bg-[#96ded1]/10">
                        <span>Phase {p.phase_number} — {p.title}</span>
                        <span className="text-[10px] text-[#4a6984]/60 font-normal italic">{p.task_type}</span>
                      </summary>
                      <div className="px-3 pb-3 pt-1 space-y-2 text-xs">
                        <p><strong>Focus:</strong> {p.focus}</p>
                        <p><strong>Applied strategies:</strong> {p.applied_strategies}</p>
                        <p><strong>Checkpoint (look-fors):</strong> {p.checkpoint}</p>
                        <details className="bg-[#f9fafb] rounded p-2 border border-[#4a6984]/10">
                          <summary className="cursor-pointer text-[#4a6984] font-bold">
                            Paste-back prompt for the lesson generator
                          </summary>
                          <p className="mt-1.5 italic text-[#1c2d3d]">{p.paste_back_prompt}</p>
                        </details>
                      </div>
                    </details>
                  ))}
                </div>
                {plan.teacher_note ? (
                  <div className="text-xs text-[#4a6984]/80 italic border-l-2 border-[#96ded1] pl-3">
                    {plan.teacher_note}
                  </div>
                ) : null}

                <div className="border-t border-[#4a6984]/10 pt-3 flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 text-xs text-[#4a6984]">
                    <input
                      type="checkbox"
                      checked={branded}
                      onChange={(e) => setBranded(e.target.checked)}
                      data-testid="isp-branded-toggle"
                      className="h-3.5 w-3.5"
                    />
                    {branded ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                    Branded
                  </label>
                  <button
                    type="button"
                    onClick={exportPdf}
                    disabled={exporting}
                    data-testid="isp-export"
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold border border-[#4a6984]/20 hover:bg-[#96ded1]/10 disabled:opacity-40"
                  >
                    {exporting ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Exporting…</>
                    ) : (
                      <><Download className="h-3 w-3 mr-1" /> Download PDF</>
                    )}
                  </button>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <input
                      type="text"
                      value={studentLabel}
                      onChange={(e) => setStudentLabel(e.target.value)}
                      placeholder="Anonymous label (e.g. Student 1)"
                      data-testid="isp-student-label"
                      className="px-2 py-1 rounded border border-[#4a6984]/20 text-xs w-44"
                    />
                    <button
                      type="button"
                      onClick={savePlan}
                      disabled={saving}
                      data-testid="isp-save"
                      className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-bold border border-[#4a6984]/20 hover:bg-[#96ded1]/10 disabled:opacity-40"
                    >
                      {saving ? (
                        <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving…</>
                      ) : (
                        <><Save className="h-3 w-3 mr-1" /> Save</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="ae-card p-4" data-testid="isp-library">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-extrabold uppercase tracking-wide">
                My plans
              </h2>
              <span className="text-[10px] text-[#4a6984]/60">
                {savedPlans.length} saved
              </span>
            </div>
            {loadingSaved ? (
              <div className="text-center py-4">
                <Loader2 className="h-4 w-4 animate-spin mx-auto text-[#4a6984]" />
              </div>
            ) : savedPlans.length === 0 ? (
              <p className="text-xs text-[#4a6984]/70 py-3">
                No saved plans yet. Generate a plan and click Save to keep it
                under an anonymous label.
              </p>
            ) : (
              <ul className="divide-y divide-[#4a6984]/10">
                {savedPlans.map((p) => (
                  <li
                    key={p.id}
                    data-testid={`isp-library-row-${p.id}`}
                    className="flex items-center justify-between py-2 gap-2"
                  >
                    <button
                      type="button"
                      onClick={() => loadSavedPlan(p.id)}
                      data-testid={`isp-library-load-${p.id}`}
                      className="text-left flex-1 hover:bg-[#96ded1]/10 rounded px-2 py-1 -ml-2"
                    >
                      <div className="text-sm font-bold">{p.student_label}</div>
                      <div className="text-[10px] text-[#4a6984]/70">
                        {p.grade} · {p.subject} · {p.reporting_period.replace(/_/g, " ")}
                      </div>
                    </button>
                    <Link
                      to={`/individualize-unit?plan=${encodeURIComponent(p.id)}`}
                      data-testid={`isp-library-individualize-${p.id}`}
                      title="Apply this plan to a unit (per-lesson overlay)"
                      className="p-1 rounded text-[#4a6984]/60 hover:text-[#1c2d3d] hover:bg-[#96ded1]/15"
                    >
                      <Layers className="h-3.5 w-3.5" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => deleteSavedPlan(p.id, p.student_label)}
                      data-testid={`isp-library-delete-${p.id}`}
                      className="p-1 rounded text-[#4a6984]/60 hover:text-rose-600 hover:bg-rose-50"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
